import { replaceHtmlEntities } from "@/shared/helpers/parse-html-value";
import { SEO_VALIDATION_LIMITS } from "./seo-validation.constants";

/**
 * Normaliza texto HTML para conteo de palabras
 * Elimina etiquetas HTML, decodifica entidades y normaliza espacios
 * @param html - Texto HTML a normalizar
 * @returns Texto normalizado sin HTML
 */
function sanitizeHtmlText(html?: string): string {
  if (!html) return "";

  return replaceHtmlEntities(html.replace(/<[^>]+>/g, " "))
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00A0/g, " ");
}

export function normalizeTextForWordCount(html?: string): string {
  if (!html) return "";

  const sanitizedText = sanitizeHtmlText(html);

  return sanitizedText
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Cuenta palabras en texto HTML normalizado
 * @param html - Texto HTML
 * @returns Número de palabras
 */
export function countWordsInHtml(html?: string): number {
  if (!html) return 0;

  const normalizedText = normalizeTextForWordCount(html);
  const words = normalizedText.split(" ").filter(word => word.length > 0);
  return words.length;
}

/**
 * Valida rango de palabras en texto HTML
 * @param html - Texto HTML a validar
 * @param minWords - Mínimo de palabras (default: 49)
 * @param maxWords - Máximo de palabras (default: 300)
 * @returns true si está en rango válido
 */
export function validateWordRange(
  html?: string,
  minWords: number = SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MIN_WORDS,
  maxWords: number = SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MAX_WORDS
): boolean {
  if (!html) return true;

  const wordCount = countWordsInHtml(html);
  return wordCount >= minWords && wordCount <= maxWords;
}

/**
 * Valida título SEO - Solo caracteres (máximo 60)
 * @param html - Texto HTML del título
 * @returns true si es válido
 */
export function validateSeoTitle(html?: string): boolean {
  if (!html) return true;

  const normalizedText = normalizeTextForHtml(html);

  return normalizedText.length <= SEO_VALIDATION_LIMITS.TITLE.MAX_LENGTH;
}

/**
 * Valida descripción corta SEO (meta description)
 * @param html - Texto HTML de la descripción corta
 * @returns true si es válido
 */
export function validateSeoShortDescription(html?: string): boolean {
  if (!html) return true;

  const normalizedText = normalizeTextForHtml(html);
  const wordCount = countWordsInHtml(html);

  return (
    normalizedText.length >= SEO_VALIDATION_LIMITS.SHORT_DESCRIPTION.MIN_LENGTH &&
    normalizedText.length <= SEO_VALIDATION_LIMITS.SHORT_DESCRIPTION.MAX_LENGTH &&
    wordCount >= SEO_VALIDATION_LIMITS.SHORT_DESCRIPTION.MIN_WORDS &&
    wordCount <= SEO_VALIDATION_LIMITS.SHORT_DESCRIPTION.MAX_WORDS
  );
}

/**
 * Valida descripción larga SEO
 * @param html - Texto HTML de la descripción larga
 * @returns true si es válido
 */
export function validateSeoLongDescription(html?: string): boolean {
  if (!html) return true;

  const normalizedText = normalizeTextForHtml(html);
  const wordCount = countWordsInHtml(html);

  return (
    normalizedText.length >= SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MIN_LENGTH &&
    normalizedText.length <= SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MAX_LENGTH &&
    wordCount >= SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MIN_WORDS &&
    wordCount <= SEO_VALIDATION_LIMITS.LONG_DESCRIPTION.MAX_WORDS
  );
}

/**
 * Normaliza texto HTML para conteo de caracteres
 * @param html - Texto HTML
 * @returns Texto normalizado sin HTML
 */
export function normalizeTextForHtml(html?: string): string {
  if (!html) return "";

  const sanitizedText = sanitizeHtmlText(html);

  return sanitizedText
    .replace(/\r\n?/g, "\n")
    .replace(/\t/g, " ")
    .replace(/\u200B/g, "")
    .trim();
}

/**
 * Valida longitud de caracteres en texto HTML
 * @param html - Texto HTML a validar
 * @param maxLength - Máximo de caracteres
 * @returns true si está dentro del límite
 */
export function validateCharacterLength(
  html?: string,
  maxLength: number = 160
): boolean {
  if (!html) return true;

  const normalizedText = normalizeTextForHtml(html);
  return normalizedText.length <= maxLength;
}
